/*
 * Inbox and Eunice shapes (draft 8).
 */
import { z } from "zod";

export const AUTHOR_WORDS = { team: "Team", eunice: "Eunice", crew: "Crew", auto: "Automatic", client: "Client" } as const;

export const threadRow = z.object({
  id: z.uuid(),
  channel: z.enum(["sms", "email"]),
  /** The client or lead's name, else their number or email. Office only. */
  name: z.string(),
  contact: z.string(),
  clientId: z.uuid().nullable(),
  status: z.enum(["open", "done"]),
  assignedTo: z.uuid().nullable(),
  unread: z.number(),
  lastAt: z.string(),
  lastText: z.string().nullable(),
  stopped: z.boolean(),
});
export type ThreadRow = z.infer<typeof threadRow>;

export const messageRow = z.object({
  id: z.uuid(),
  direction: z.enum(["in", "out", "note"]),
  author: z.enum(["team", "eunice", "crew", "auto", "client"]),
  /** "From Ann L. (crew)" and the like. */
  from: z.string(),
  body: z.string(),
  at: z.string(),
  status: z.string().nullable(),
});
export type MessageRow = z.infer<typeof messageRow>;

export const threadDetail = threadRow.extend({ messages: z.array(messageRow) });
export type ThreadDetail = z.infer<typeof threadDetail>;

/** What Undo send gives back: the words, so they go back in the reply box. */
export const undoneMessage = z.object({ body: z.string() });
export type UndoneMessage = z.infer<typeof undoneMessage>;

export const replyBody = z.object({ body: z.string().trim().min(1, "Type a message.").max(1500) }).strict();
export const threadPatch = z
  .object({ status: z.enum(["open", "done"]).optional(), assignedTo: z.uuid().nullable().optional() })
  .strict();

/** A crew member writing to today's or tomorrow's client, from the business line. */
export const crewMessageBody = z.object({ body: z.string().trim().min(1).max(600) }).strict();
export const crewThread = z.object({
  messages: z.array(z.object({ mine: z.boolean(), fromClient: z.boolean(), body: z.string(), at: z.string() })),
  canWrite: z.boolean(),
});
export type CrewThread = z.infer<typeof crewThread>;

export const lessonRow = z.object({
  id: z.uuid(),
  text: z.string(),
  status: z.enum(["proposed", "approved", "rejected"]),
  createdAt: z.string(),
});
export type LessonRow = z.infer<typeof lessonRow>;
export const lessonInput = z.object({ text: z.string().trim().min(5).max(500) }).strict();
export const lessonPatch = z.object({ status: z.enum(["approved", "rejected"]) }).strict();

export const euniceRun = z.object({
  id: z.uuid(),
  threadId: z.uuid(),
  contact: z.string(),
  mode: z.string(),
  category: z.string().nullable(),
  reply: z.string().nullable(),
  sent: z.boolean(),
  alert: z.boolean(),
  alertSummary: z.string().nullable(),
  skipped: z.string().nullable(),
  at: z.string(),
});
export type EuniceRun = z.infer<typeof euniceRun>;
export const euniceAnswer = z.object({
  runs: z.array(euniceRun),
  lessons: z.array(lessonRow),
  usedThisMonth: z.number(),
  allowance: z.number(),
  webhookUrl: z.string().nullable(),
});
export type EuniceAnswer = z.infer<typeof euniceAnswer>;
